import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
} from "@win-reality/react-ui";
import { useEffect, useState } from "react";

import homePlateOutline from "@/assets/home-plate-outline.png";

import { Slider } from "./Slider";

const DEFAULT_INTERCEPT = 8.5;
const MAX_INTERCEPT = 17;

// Approximate fractions within the PNG where the flat top edge and tip sit
const TOP_FRACTION = 0.12;
const BOTTOM_FRACTION = 0.855;

const PLATE_HEIGHT_PX = 200;

interface EditInterceptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: number;
  onSave: (v: number) => void | Promise<void>;
}

export const EditInterceptModal = ({
  open,
  onOpenChange,
  value,
  onSave,
}: EditInterceptModalProps) => {
  const [localValue, setLocalValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) setLocalValue(value);
  }, [open, value]);

  const lineFraction =
    BOTTOM_FRACTION -
    (localValue / MAX_INTERCEPT) * (BOTTOM_FRACTION - TOP_FRACTION);
  const lineTopPx = lineFraction * PLATE_HEIGHT_PX;

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await onSave(localValue);
      onOpenChange(false);
    } catch {
      // Error toast is handled by the mutation hook
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      defaultCancelButton={false}
      size="lg"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>Strike Zone Intercept</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col items-center gap-6 font-manrope">
              <p className="text-sm text-gray-600">
                Your default strike zone is located at 8.5 inches from the back
                of home plate. You can change this to any position on the plate.
              </p>

              {/* Home plate visualization */}
              <div
                className="relative"
                style={{ height: `${PLATE_HEIGHT_PX}px`, width: "auto" }}
              >
                <img
                  src={homePlateOutline}
                  alt="Home plate"
                  style={{ height: `${PLATE_HEIGHT_PX}px`, width: "auto" }}
                  className="object-contain"
                />
                {/* Dashed intercept line */}
                <div
                  className="absolute left-0 right-0"
                  style={{
                    top: `${lineTopPx}px`,
                    borderTop: "2px dashed #2563eb",
                  }}
                />
              </div>

              {/* Slider + value + reset */}
              <div className="flex w-full max-w-md items-center gap-3 border border-gray-200 rounded-xl px-4 py-3">
                <Slider
                  value={localValue}
                  onChange={setLocalValue}
                  min={0}
                  max={MAX_INTERCEPT}
                  step={0.1}
                  variant="continuous"
                />
                <span className="text-sm font-mono text-gray-600 w-12 text-right shrink-0">
                  {localValue.toFixed(1)}&quot;
                </span>
                <button
                  type="button"
                  onClick={() => setLocalValue(DEFAULT_INTERCEPT)}
                  className="text-blue text-sm font-medium hover:underline shrink-0"
                >
                  Reset
                </button>
              </div>
            </div>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={handleConfirm}
                isLoading={isSaving}
              >
                Set
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
